/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { useNavigate } from 'react-router-dom'
import dayjs from 'dayjs'

import { View } from '@components/atoms'
import { boShared } from '@components/systems/backoffice/backoffice.tokens'
import { BoStatTiles, BoStatTile } from '@components/systems/backoffice/BoStatTiles'
import { BoSparkline } from '@components/systems/backoffice/BoSparkline'
import { BoBars, BoBarRow } from '@components/systems/backoffice/BoBars'
import { BoSegments } from '@components/systems/backoffice/BoSegments'

import { dashboardApi, ResType } from '@api'
import {
	useAppSelector,
	useAppDispatch,
	orderActions,
	inquiryMgmtActions,
	UserMenuAccessType,
	MenuType,
} from '@redux/index'
import type {
	DashboardRange,
	DashboardSummaryType,
	DashboardSalesLineType,
	DashboardAnalyticsType,
} from '@schemas'

/**
 * /main/home — 백오피스 대시보드 (judahsWeb/ANALYTICS.md §10 · DESIGN_SYSTEM §13.2).
 * 상단 줄(기간 칩 · 갱신 · 새로고침) + 구역 다섯(할 일 · 판매 · 트래픽 GA4 · 기기 · 시스템), 구역마다 제목 한 줄. 로고·설명 문장은 없다(2026-09-28 사용자 결정).
 * 데이터는 `GET /dashboard/summary` · `GET /dashboard/analytics` 두 번이고, GA 는 서버가 읽어 캐시한다.
 * 밴드는 메뉴 권한(R)으로 가린다 — 매출·트래픽은 회계(정산) 권한자만.
 * 이 파일에 @media 는 없다 — 폭 대응은 BoStatTiles · 그리드 auto-fit 이 한다(RESPONSIVE_PLAN §2).
 */

const RANGES: { id: DashboardRange; label: string }[] = [
	{ id: 'today', label: '오늘' },
	{ id: '7d', label: '7일' },
	{ id: '28d', label: '28일' },
	// 열린 시즌의 시작일 ~ 오늘. 시즌 판매 추이를 그 폭으로 본다 (2026-09-29 사용자 지시)
	{ id: 'season', label: '이번 시즌' },
]
const RANGE_KEY = 'judahs_bo_home_range'
const ANALYTICS_POLL_MS = 60 * 1000

const nf = new Intl.NumberFormat('en-US')
const count = (n: number | undefined): string => (n === undefined ? '—' : nf.format(n))
/** 판매가 표기 규칙(PRICING): `.00` 을 붙이지 않고, 달러·원화를 환산하지 않는다 */
const money = (currency: string, n: number): string =>
	currency === 'KRW'
		? `₩${nf.format(Math.round(n))}`
		: `$${new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(n)}`
const dayLabel = (ymd: string): string => dayjs(ymd).format('M/D')
const FUNNEL_LABEL: { [key: string]: string } = {
	view_item: '상품 보기',
	add_to_cart: '장바구니',
	begin_checkout: '결제 시작',
	purchase: '구매',
	generate_lead: '문의 전송',
	sign_up: '가입',
}
const MODEL_LABEL: { [key: string]: string } = {
	pls8: 'Koinos PLS 8',
	mcs6: 'Kedem 6',
	mc4: 'Kedem 4',
}

/** 메뉴 트리(폴더 → 항목)를 한 줄로 편다 — 루트는 폴더 4개뿐이라(2026-09-28) 항목은 전부 children 안에 있다 */
const flattenMenu = (list: MenuType[]): MenuType[] =>
	list.reduce<MenuType[]>(
		(acc, m) => acc.concat(m, m.children !== undefined ? flattenMenu(m.children) : []),
		[],
	)

const lastSegment = (path: string | undefined): string =>
	(path ?? '').split('/').filter(Boolean).pop() ?? ''

const readRange = (): DashboardRange => {
	try {
		const v = window.localStorage.getItem(RANGE_KEY)
		return RANGES.some(r => r.id === v) ? (v as DashboardRange) : '7d'
	} catch {
		return '7d'
	}
}

const HomePage = () => {
	const dispatch = useAppDispatch()
	const navigate = useNavigate()

	const accessList: UserMenuAccessType[] = useAppSelector(
		state => state.auth.userAccessMenuList,
	)
	const menu: MenuType[] = useAppSelector(state => state.main.menu)
	/**
	 * 라우트(`/main/order`)로 읽기 권한을 묻는다. 권한 목록의 `uri` 는 메뉴 경로(`/orders/order`)라 라우트와 다르다 —
	 * AccessControlGuard 처럼 메뉴 id 로 잇는다(메뉴 `to` = 라우트).
	 */
	const flatMenu = React.useMemo(() => flattenMenu(menu), [menu])
	const canRead = React.useCallback(
		(route: string): boolean => {
			const ids = flatMenu.filter(m => m.to === route).map(m => m.id)
			// 메뉴 `to` 가 라우트와 다를 때(메뉴 관리에서 트리를 저장하면 URI 가 PATH 로 바뀐다 — PENDING §81.2)
			// 권한 목록의 uri 마지막 조각으로도 잇는다: /orders/order ↔ /main/order, /partners/dealer-price ↔ /main/dealer-price
			const tail = lastSegment(route)
			return accessList.some(
				m =>
					String(m.crud ?? '').includes('R') &&
					(ids.includes(m.menuId) || (tail !== '' && lastSegment(m.uri) === tail)),
			)
		},
		[accessList, flatMenu],
	)
	const canOrder = canRead('/main/order')
	const canInquiry = canRead('/main/inquiry')
	const canSettlement = canRead('/main/settlement')
	const canSeason = canRead('/main/season')

	const [range, setRange] = React.useState<DashboardRange>(readRange)
	const [summary, setSummary] = React.useState<DashboardSummaryType | null>(null)
	const [analytics, setAnalytics] = React.useState<DashboardAnalyticsType | null>(null)
	const [summaryStatus, setSummaryStatus] = React.useState<'loading' | 'ok' | 'error'>(
		'loading',
	)
	const [refreshing, setRefreshing] = React.useState<boolean>(false)
	const rangeRef = React.useRef<DashboardRange>(range)
	rangeRef.current = range

	const loadSummary = React.useCallback(async (r: DashboardRange) => {
		const res: ResType | undefined = await dashboardApi.getDashboardSummary({
			query: { range: r },
		})
		if (rangeRef.current !== r) return
		if (
			res !== undefined &&
			res.status === 200 &&
			Array.isArray(res.data) &&
			res.data[0]
		) {
			setSummary(res.data[0] as DashboardSummaryType)
			setSummaryStatus('ok')
		} else {
			setSummaryStatus('error')
		}
	}, [])

	const loadAnalytics = React.useCallback(async (r: DashboardRange) => {
		const res: ResType | undefined = await dashboardApi.getDashboardAnalytics({
			query: { range: r },
		})
		if (rangeRef.current !== r) return
		if (
			res !== undefined &&
			res.status === 200 &&
			Array.isArray(res.data) &&
			res.data[0]
		) {
			setAnalytics(res.data[0] as DashboardAnalyticsType)
		}
	}, [])

	const loadAll = React.useCallback(
		async (r: DashboardRange) => {
			setRefreshing(true)
			await Promise.all([
				loadSummary(r),
				canSettlement ? loadAnalytics(r) : Promise.resolve(),
			])
			setRefreshing(false)
		},
		[loadSummary, loadAnalytics, canSettlement],
	)

	// 기간이 바뀌면 둘 다 다시 읽는다. 기간은 이 브라우저에 기억한다
	React.useEffect(() => {
		try {
			window.localStorage.setItem(RANGE_KEY, range)
		} catch {
			/* 저장 못 해도 이번 화면은 그대로 */
		}
		void loadAll(range)
	}, [range, loadAll])

	// 탭으로 돌아오면 다시 읽고, 보이는 동안 GA 실시간 수만 60초마다 갱신한다(서버 캐시라 비용이 없다)
	React.useEffect(() => {
		const onVisible = () => {
			if (document.visibilityState === 'visible') void loadAll(rangeRef.current)
		}
		document.addEventListener('visibilitychange', onVisible)
		const timer = window.setInterval(() => {
			if (document.visibilityState === 'visible' && canSettlement) {
				void loadAnalytics(rangeRef.current)
			}
		}, ANALYTICS_POLL_MS)
		return () => {
			document.removeEventListener('visibilitychange', onVisible)
			window.clearInterval(timer)
		}
	}, [loadAll, loadAnalytics, canSettlement])

	/**
	 * 할 일 → 오더 관리. 개수가 전체 기간 기준이라 목록도 전체 기간으로 연다(기간 시작을 넉넉히 뒤로).
	 * 상태·채널은 SEARCH_STATUS 사가가 필터 기억(localStorage)에 적어 두고, 기간은 저장하지 않는다.
	 */
	const openOrders = (status: string) => {
		dispatch(orderActions.SEARCH_MODE('date'))
		dispatch(orderActions.SEARCH_START_DATE('2020-01-01'))
		dispatch(orderActions.SEARCH_END_DATE(dayjs().format('YYYY-MM-DD')))
		dispatch(orderActions.SEARCH_CHANNEL('all'))
		dispatch(orderActions.SEARCH_STATUS(status))
		navigate('/main/order')
	}
	const openInquiries = () => {
		dispatch(inquiryMgmtActions.STATUS_FILTER('pending'))
		navigate('/main/inquiry')
	}

	// ── 01 할 일
	const q = summary?.queue
	const queueTiles: BoStatTile[] = []
	if (canOrder) {
		queueTiles.push(
			{
				label: '신규 주문',
				value: count(q?.newOrders),
				onClick: () => openOrders('new'),
			},
			{
				label: '입금 대기 · 도매',
				value: count(q?.awaitingPayment),
				onClick: () => openOrders('awaitingPayment'),
			},
			{
				label: '배송 준비중',
				value: count(q?.preparing),
				onClick: () => openOrders('preparing'),
			},
			{
				label: '배송중',
				value: count(q?.inTransit),
				onClick: () => openOrders('inTransit'),
			},
			{
				label: '검토 필요',
				value: count(q?.review),
				note: '청구액 어긋남 · 분쟁',
				onClick: () => openOrders('all'),
			},
		)
		// 프리오더 경고 둘 — 있을 때만 선다 (ORDER_PLAN §3.1)
		if ((q?.preorderUnconfirmed ?? 0) > 0) {
			queueTiles.push({
				label: '프리오더 확인 누락',
				value: count(q?.preorderUnconfirmed),
				note: '생산 시작 · 주문확인 전',
				onClick: () => openOrders('new'),
			})
		}
		if ((q?.preorderUnshipped ?? 0) > 0) {
			queueTiles.push({
				label: '프리오더 송장 누락',
				value: count(q?.preorderUnshipped),
				note: '출고 종료 · 송장 없음',
				onClick: () => openOrders('preparing'),
			})
		}
	}
	if (canInquiry) {
		queueTiles.push({
			label: '열린 문의',
			value: count(q?.openInquiries),
			onClick: openInquiries,
		})
	}
	if (canSettlement) {
		queueTiles.push({
			label: '정산 미납',
			value: count(q?.settlementDue),
			note:
				q !== undefined && q.settlementDue > 0
					? `${q.settlementOldestMonth}부터 ${q.settlementDueMonths}개월`
					: '지난달까지',
			onClick: () => navigate('/main/settlement'),
		})
	}

	// ── 02 판매
	const sales = summary?.sales
	const lines: DashboardSalesLineType[] = sales?.lines ?? []
	const sumBy = (pick: (l: DashboardSalesLineType) => number) =>
		lines.reduce<number>((s, l) => s + Number(pick(l)), 0)
	/**
	 * 통화별 합계. 서버가 이미 환불 건을 빼고 준다 — 건수 · 수량 · USD · KRW 전부 같다
	 * (2026-09-29 사용자 지시). 여기서 다시 빼지 않는다.
	 */
	const byCurrency = (cur: string) =>
		lines
			.filter(l => l.currency === cur)
			.reduce<number>((s, l) => s + Number(l.paidSum), 0)
	const byChannel = (ch: string) =>
		lines
			.filter(l => l.channel === ch)
			.reduce<number>((s, l) => s + Number(l.orderCount), 0)
	const refundCount = sumBy(l => l.refundCount)
	/** 네 타일 모두 환불 건을 뺀 값이다 — 뺀 건수는 한 번만 말한다 */
	const refundNote =
		sales === undefined
			? undefined
			: refundCount > 0
			? `환불 ${count(refundCount)}건 제외`
			: '환불 0건'
	const salesTiles: BoStatTile[] = [
		{
			label: '결제 주문',
			value: sales === undefined ? '—' : count(sumBy(l => l.orderCount)),
			note:
				sales === undefined
					? undefined
					: `소매 ${count(byChannel('retail'))} · 도매 ${count(byChannel('wholesale'))}`,
		},
		{
			label: '판매 수량',
			value: sales === undefined ? '—' : count(sumBy(l => l.unitCount)),
			note: refundNote,
		},
		{
			label: 'USD 합계',
			value: sales === undefined ? '—' : money('USD', byCurrency('USD')),
		},
		{
			label: 'KRW 합계',
			value: sales === undefined ? '—' : money('KRW', byCurrency('KRW')),
		},
	]
	const season = summary?.season ?? null
	const seasonRows: BoBarRow[] = (season?.products ?? []).map(p => ({
		label: p.name,
		value: p.sold,
		max: Math.max(1, p.quantity),
		note: `/ ${count(p.quantity)}`,
	}))

	// ── 03 트래픽
	const a = analytics
	const gaOk = a !== null && a.available
	const gaTiles: BoStatTile[] = [
		{ label: '세션', value: gaOk ? count(a.totals.sessions) : '—' },
		{ label: '사용자', value: gaOk ? count(a.totals.users) : '—' },
		{ label: '페이지뷰', value: gaOk ? count(a.totals.pageViews) : '—' },
		{
			label: '지금 활성 · 30분',
			value: a?.realtime ? count(a.realtime.activeUsers) : '—',
			note: gaOk ? `주요 이벤트 ${count(a.totals.keyEvents)}` : undefined,
		},
	]
	const funnelRows: BoBarRow[] = gaOk
		? a.funnel
				.filter(f =>
					['view_item', 'add_to_cart', 'begin_checkout', 'purchase'].includes(f.event),
				)
				.map(f => ({ label: FUNNEL_LABEL[f.event] ?? f.event, value: f.count }))
		: []
	const otherEvents = gaOk
		? a.funnel.filter(f => ['generate_lead', 'sign_up'].includes(f.event))
		: []
	const gaReason =
		a === null
			? ''
			: a.reason === 'not_configured'
			? '서버에 GA 설정(GA_PROPERTY_ID · GA_WIF_AUDIENCE · GA_SERVICE_ACCOUNT)이 없다.'
			: a.reason

	// ── 04 기기
	const dv = summary?.devices
	const deviceTiles: BoStatTile[] = [
		{ label: '보고된 기기', value: count(dv?.total) },
		{ label: '최근 7일 활동', value: count(dv?.active7d) },
	]
	const modelRows: BoBarRow[] = (dv?.byModel ?? []).map(m => ({
		label: MODEL_LABEL[m.model] ?? m.model,
		value: m.count,
		note: `7일 ${count(m.active7d)}`,
	}))
	const fwRows: BoBarRow[] = (dv?.byFwVersion ?? []).slice(0, 6).map(f => ({
		label: f.fwVersion === '' ? '버전 없음' : `v${f.fwVersion}`,
		value: f.count,
	}))

	// ── 05 시스템
	const sys = summary?.system
	const updatedLabel =
		summary !== null
			? dayjs(summary.updatedAt).format('HH:mm:ss')
			: summaryStatus === 'error'
			? '실패'
			: '…'
	const window_ = summary !== null ? `${summary.from} – ${summary.to}` : ''
	const rangeLabel: string = RANGES.find(r => r.id === range)?.label ?? ''
	/**
	 * 「이번 시즌」 은 폭이 시즌마다 달라 이름을 같이 적는다. 열린 시즌이 없으면 서버가 28일 폭으로
	 * 물러나므로 그 사실을 말한다 — 기간만 보이면 왜 28일인지 알 수 없다.
	 */
	const seasonWindowNote: string =
		range !== 'season' || summary === null
			? ''
			: (summary.seasonTitle ?? '') !== ''
			? ` · ${summary.seasonTitle}`
			: ' · 열린 시즌이 없어 최근 28일'

	return (
		<View css={style}>
			<View className="boWrap">
				<View className="homeBar">
					<BoSegments
						variant="chips"
						items={RANGES}
						value={range}
						onChange={id => setRange(id as DashboardRange)}
					/>
					<span className="homeUpdated">
						<i className="homeLive" aria-hidden="true" />
						{window_}
						{seasonWindowNote}
						{window_ !== '' ? ' · ' : ''}갱신 {updatedLabel}
					</span>
					<button
						type="button"
						className="boBtnText homeRefresh"
						disabled={refreshing}
						onClick={() => void loadAll(range)}
					>
						새로고침
					</button>
				</View>

				{summaryStatus === 'error' ? (
					<p className="homeError">
						대시보드 데이터를 받지 못했다. 새로고침으로 다시 시도한다.
					</p>
				) : null}

				{queueTiles.length > 0 ? (
					<section className="homeSection">
						<h2 className="homeTitle">할 일</h2>
						<View className="homeCard">
							<BoStatTiles items={queueTiles} dense />
						</View>
					</section>
				) : null}

				{canSettlement || canOrder || canSeason ? (
					<section className="homeSection">
						<h2 className="homeTitle">판매</h2>
						{canSettlement ? (
							<View className="homeCard">
								<BoStatTiles items={salesTiles} />
							</View>
						) : null}
						<View className="homeTwoCol">
							{canSettlement ? (
								<View className="homeCard">
									<BoSparkline
										caption={`일별 결제 주문 · ${rangeLabel}`}
										points={(sales?.daily ?? []).map(d => ({
											label: dayLabel(d.date),
											value: d.orders,
										}))}
									/>
								</View>
							) : null}
							{canOrder || canSeason ? (
								<View className="homeCard">
									<BoBars
										caption="시즌 판매 · 결제 수량 / 시즌 수량"
										subcaption={
											season !== null
												? `${season.title} · ${season.startDate} – ${season.endDate}${
														season.targetQuantity > 0
															? ` · 목표 ${count(season.targetQuantity)}대`
															: ''
												  }`
												: undefined
										}
										rows={seasonRows}
										emptyText={
											season === null ? '열린 시즌이 없다' : '시즌에 상품이 없다'
										}
									/>
								</View>
							) : null}
						</View>
					</section>
				) : null}

				{canSettlement ? (
					<section className="homeSection">
						<h2 className="homeTitle">트래픽 · Google Analytics</h2>
						{a !== null && !a.available ? (
							<p className="homeGaOff">
								Google Analytics 에 연결되지 않았다.
								{gaReason !== '' ? (
									<span className="homeGaReason"> {gaReason}</span>
								) : null}
							</p>
						) : (
							<>
								<View className="homeCard homeStack">
									<BoStatTiles items={gaTiles} />
									<BoSparkline
										caption={`Google Analytics · judahs.io 일별 세션 · ${rangeLabel}${
											a !== null && a.stale
												? ` · ${dayjs(a.updatedAt).format('HH:mm')} 값`
												: ''
										}`}
										points={(a?.daily ?? []).map(d => ({
											label: dayLabel(d.date),
											value: d.sessions,
										}))}
									/>
								</View>
								<View className="homeCard">
									<View className="homeGaCols">
										<View className="homeStack">
											<BoBars caption="구매 퍼널 · 이벤트 수" rows={funnelRows} />
											<BoBars
												caption="채널 · 세션"
												rows={(a?.channels ?? []).map(c => ({
													label: c.name,
													value: c.sessions,
												}))}
											/>
										</View>
										<BoBars
											caption="상위 페이지 · 페이지뷰"
											rows={(a?.pages ?? []).map(p => ({
												label: p.path,
												value: p.views,
											}))}
										/>
										<BoBars
											caption="국가 · 세션"
											rows={(a?.countries ?? []).map(c => ({
												label: c.name,
												value: c.sessions,
											}))}
										/>
									</View>
								</View>
								{gaOk ? (
									<p className="homeLine">
										{otherEvents
											.map(e => `${FUNNEL_LABEL[e.event] ?? e.event} ${count(e.count)}`)
											.join(' · ')}
										{a.hosts.length > 0 ? (
											<>
												{otherEvents.length > 0 ? ' · ' : ''}호스트{' '}
												{a.hosts.map(h => `${h.host} ${count(h.sessions)}`).join(' · ')}
											</>
										) : null}
										{' · 쿠키에 동의한 방문만 집계된다'}
									</p>
								) : null}
							</>
						)}
					</section>
				) : null}

				<section className="homeSection">
					<h2 className="homeTitle">기기 · Studio 텔레메트리</h2>
					<View className="homeCard">
						<View className="homeThreeCol">
							<BoStatTiles items={deviceTiles} className="homeDeviceTiles" />
							<BoBars
								caption="기기 · 모델"
								rows={modelRows}
								emptyText="보고된 기기가 없다"
							/>
							<BoBars
								caption="기기 · 펌웨어 버전"
								rows={fwRows}
								emptyText="보고된 기기가 없다"
							/>
						</View>
					</View>
				</section>

				<section className="homeSection last">
					<h2 className="homeTitle">시스템</h2>
					<p className="homeFoot">
						API {sys !== undefined ? `v${sys.apiVersion}` : '—'}
						{sys !== undefined && sys.nodeId !== '' ? ` · 노드 ${sys.nodeId}` : ''}
						{' · 오늘 에러 '}
						{count(sys?.errorsToday)}
						{sys !== undefined ? ` (로그 ${sys.logDate})` : ''}
						{summary !== null
							? ` · 집계 ${dayjs(summary.updatedAt).format('YYYY-MM-DD HH:mm:ss')}`
							: ''}
					</p>
				</section>
			</View>
		</View>
	)
}

export default HomePage

/**
 * `/main/home` 라이트 (2026-09-29 사용자 결정 — B안, judahsWeb/PENDING_CHANGES.md §84.5).
 *
 * 베이스는 화이트·블랙이고 **유채색은 블루 하나(`acc`)뿐이다** — 데이터 마크와 활성 세그먼트에만 쓴다.
 * 벤치마크는 Vercel·Stripe 계열의 데이터 화면. 쉘(사이드바·상단 탭 바)과 톤이 이어져 경계가 생기지 않는다.
 *
 * 차트 규격은 `dataviz` 스킬을 따른다: 선 2px · 면은 같은 색 10~20% 워시 · 끝점 지름 8 + 서피스 링 2 ·
 * 막대 끝 4px 라운드(밑단은 각) · 트랙은 같은 램프의 옅은 단계 · 큰 수는 비례숫자, 열로 서는 숫자만 `tabular-nums`.
 * 이 파일에 `@media` 는 없다 — 폭 대응은 컴포넌트가 한다(RESPONSIVE_PLAN §2).
 */
const pal = {
	bg: '#FAFAFA',
	surface: '#FFFFFF',
	line: '#E8EAED',
	line2: '#EEF0F3',
	ink: '#0C0D0F',
	dim: '#5B626D',
	faint: '#9298A3',
	/**
	 * 포인트 — 이 화면의 유일한 유채색 (2026-09-29 사용자 지정).
	 * 흰 면 대비 4.6 · `bg` 대비 4.41 이라 **13px 이상 본문에만** 글자로 쓴다(세그먼트 라벨 12px 는 500 굵기).
	 * 관리자 모드 띠(`boAdmin.bar`)와 같은 값이다 — 관리자 모드에서는 띠와 데이터 색이 같은 파랑으로 선다.
	 */
	acc: '#1976D2',
	accWash: 'rgba(25, 118, 210, 0.09)',
	accSoft: 'rgba(25, 118, 210, 0.13)',
	error: '#B42318',
} as const

const style = css({
	...boShared,
	minHeight: '100%',
	paddingBottom: 56,
	background: pal.bg,
	color: pal.ink,

	/** 상단 줄 — 기간 세그먼트 · 갱신 · 새로고침 */
	'.homeBar': {
		display: 'flex',
		alignItems: 'center',
		flexWrap: 'wrap',
		gap: '10px 14px',
		padding: '18px 0 22px',
		// 세그먼트 컨트롤 — 칩 셋을 한 덩어리로 묶는다. BoSegments 의 chips 규칙을 이겨야 해서 선택자를 맞춘다
		'.boSegments.chips': {
			flex: '0 1 auto',
			gap: 0,
			border: `1px solid ${pal.line}`,
			borderRadius: 6,
			overflow: 'hidden',
		},
		'.boSegments.chips .boSeg': {
			height: 30,
			border: 0,
			borderLeft: `1px solid ${pal.line}`,
			borderRadius: 0,
			background: 'transparent',
			color: pal.dim,
			fontSize: 12,
			fontWeight: 500,
			'&:first-of-type': { borderLeft: 0 },
			'&:hover': { color: pal.ink },
		},
		'.boSegments.chips .boSeg.active': {
			background: pal.accWash,
			color: pal.acc,
			fontWeight: 600,
		},
	},
	'.homeUpdated': {
		display: 'inline-flex',
		alignItems: 'center',
		fontSize: 12,
		color: pal.faint,
		fontVariantNumeric: 'tabular-nums',
		whiteSpace: 'nowrap',
	},
	/** 살아 있다는 표시 한 점 */
	'.homeLive': {
		display: 'inline-block',
		width: 6,
		height: 6,
		marginRight: 8,
		borderRadius: '50%',
		background: pal.acc,
		boxShadow: `0 0 0 3px ${pal.accWash}`,
		flex: '0 0 auto',
	},
	'.homeRefresh': {
		marginLeft: 'auto',
		height: 30,
		padding: '0 14px',
		border: `1px solid ${pal.line}`,
		borderRadius: 6,
		color: pal.dim,
		'&:hover:not(:disabled)': { color: pal.ink, borderColor: pal.faint },
	},

	'.homeError': {
		padding: '12px 14px',
		fontSize: 13,
		color: pal.error,
		border: `1px solid ${pal.error}33`,
		borderRadius: 6,
	},

	/** 구역 = 제목 한 줄 + 카드 */
	'.homeSection': {
		display: 'flex',
		flexDirection: 'column',
		gap: 12,
		padding: '14px 0 18px',
	},
	'.homeTitle': {
		display: 'flex',
		alignItems: 'center',
		gap: 14,
		fontSize: 11,
		fontWeight: 600,
		letterSpacing: '.18em',
		textTransform: 'uppercase',
		lineHeight: 1,
		color: pal.faint,
		whiteSpace: 'nowrap',
		'&::after': { content: '""', flex: 1, height: 1, background: pal.line },
	},

	/** 카드 — 1px 선과 면으로만. 그림자·블러 없음 */
	'.homeCard': {
		background: pal.surface,
		border: `1px solid ${pal.line}`,
		borderRadius: 6,
		boxShadow: '0 1px 2px rgba(16, 24, 40, 0.04)',
		minWidth: 0,
		overflow: 'hidden',
	},
	/** 카드 안에서 직접 내용을 쓰는 것(스파크라인·막대)만 패딩을 갖는다. 타일은 칸마다 제 패딩이 있다 */
	'.homeCard > .boSparkline, .homeCard > .boBars': { padding: '18px 22px' },
	'.homeCard.homeStack': { padding: 0 },
	'.homeCard.homeStack > .boSparkline': { padding: '0 22px 18px' },

	/** 지표 칸 — 세로 규칙선이 열을 만든다. 정산 화면의 타일은 건드리지 않게 이 페이지 안에서만 */
	'.homeCard .boStatTiles': { gap: 0 },
	'.homeCard .boStatTile': {
		padding: '16px 22px',
		borderLeft: `1px solid ${pal.line2}`,
		gap: 8,
		'&:first-child': { borderLeft: 0 },
	},
	'.homeCard .boStatTileLabel': {
		fontSize: 11,
		fontWeight: 500,
		letterSpacing: 0,
		textTransform: 'none',
		color: pal.dim,
	},
	/** 큰 수는 비례숫자다 — `tabular-nums` 는 열로 설 때만(dataviz) */
	'.homeCard .boStatTileValue': {
		fontSize: 30,
		fontWeight: 600,
		letterSpacing: '-0.025em',
		fontVariantNumeric: 'normal',
		color: pal.ink,
	},
	'.homeCard .boStatTileNote': { fontSize: 11, color: pal.faint },
	'.homeCard .boStatTileBtn': {
		transition: 'background-color 160ms ease-out',
		'&:hover': { background: pal.accWash },
		'&:hover .boStatTileLabel': { color: pal.dim },
		'&:hover .boStatTileValue': { textDecoration: 'none' },
		'&:focus-visible': { outline: `1px solid ${pal.acc}`, outlineOffset: -1 },
	},

	/**
	 * 순위 막대 — 한 색(블루) + 길이. 트랙은 같은 색의 옅은 단계.
	 * 🔴 선택자를 `.homeCard` 아래에 둔다. 페이지 루트 하나만 붙이면 컴포넌트의 `.css-xxx .boBarFill` 과
	 * 특이도가 동률이라, 나중에 주입되는 컴포넌트 쪽이 이겨 전부 `mono.ink`(검정)로 그려진다.
	 * 2026-09-29 운영 2.37.0 에서 실제로 그렇게 나갔다(PENDING §84.7).
	 */
	'.homeCard .boBarsCaption': {
		fontSize: 12,
		fontWeight: 500,
		letterSpacing: 0,
		textTransform: 'none',
		color: pal.ink,
		marginBottom: 12,
	},
	'.homeCard .boBarsSub': { color: pal.faint },
	'.homeCard .boBarRow': { borderBottom: 0, minHeight: 30, padding: '3px 0' },
	'.homeCard .boBarLabel': { color: pal.dim, fontSize: 12 },
	'.homeCard .boBarTrack': { height: 8, background: pal.accSoft, borderRadius: 2 },
	'.homeCard .boBarFill': {
		background: pal.acc,
		opacity: 1,
		borderRadius: '0 4px 4px 0',
	},
	'.homeCard .boBarValue': { color: pal.ink, fontSize: 12 },
	'.homeCard .boBarNote': { color: pal.faint },
	'.homeCard .boBarsEmpty': { color: pal.faint },
	'.homeCard .boBarRow.clickable:hover': { background: pal.accWash },

	/** 스파크라인 — 선 2px, 면은 같은 색 워시, 끝점 8 + 서피스 링 */
	'.homeCard .boSparkCaption': {
		fontSize: 12,
		fontWeight: 500,
		letterSpacing: 0,
		textTransform: 'none',
		color: pal.ink,
	},
	'.homeCard .boSparkSummary': { color: pal.dim, fontSize: 12 },
	'.homeCard .boSparkLabel': { color: pal.faint },
	'.homeCard .boSparkStopA': { stopColor: pal.acc, stopOpacity: 0.18 },
	'.homeCard .boSparkStopB': { stopColor: pal.acc, stopOpacity: 0 },
	'.homeCard .boSparkPath': { stroke: pal.acc, strokeWidth: 2 },
	'.homeCard .boSparkCross': { stroke: pal.line },
	'.homeCard .boSparkDot': { background: pal.acc, boxShadow: `0 0 0 2px ${pal.surface}` },
	'.homeCard .boSparkEmpty': { borderBottom: `1px solid ${pal.line}` },

	'.homeTwoCol': {
		display: 'grid',
		gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 340px), 1fr))',
		gap: 12,
		alignItems: 'stretch',
	},
	/** GA 목록 셋 — 카드 하나 안에서 세로 규칙선으로 나눈다 */
	'.homeGaCols': {
		display: 'grid',
		gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 300px), 1fr))',
		alignItems: 'start',
		'> *': { padding: '18px 22px', borderLeft: `1px solid ${pal.line2}`, minWidth: 0 },
		'> *:first-of-type': { borderLeft: 0 },
	},
	'.homeStack': { display: 'flex', flexDirection: 'column', gap: 24, minWidth: 0 },
	'.homeThreeCol': {
		display: 'grid',
		gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 260px), 1fr))',
		alignItems: 'start',
		'> *': { padding: '18px 22px', borderLeft: `1px solid ${pal.line2}`, minWidth: 0 },
		'> *:first-of-type': { borderLeft: 0, padding: 0 },
	},
	'.homeDeviceTiles': { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' },

	'.homeLine': { fontSize: 12, fontWeight: 300, lineHeight: 1.6, color: pal.faint },
	'.homeGaOff': { fontSize: 13, fontWeight: 300, color: pal.dim, padding: '18px 22px' },
	'.homeGaReason': { color: pal.faint },
	'.homeFoot': { fontSize: 12, color: pal.faint, fontVariantNumeric: 'tabular-nums' },
	'.homeFoot b': { fontWeight: 500, color: pal.dim },
})
